/**
 * Server functions and utilities for the Portals app.
 *
 * tRPC-backed queries and mutations are now accessed via the typed `trpc`
 * proxy from `@/lib/trpc`:
 *
 *   import { trpc } from '@/lib/trpc';
 *   trpc.portal.getAuthor.queryOptions({ portalId, locale })
 *   trpc.payments.preregisterDonation.mutationOptions()
 *
 * This file retains:
 * - Direct server functions (not suitable for tRPC: streaming, etc.)
 * - Utility functions (ensureSeoData, extractErrorInfo, currentWindowKey)
 */

// ── Imports ──────────────────────────────────────────────────────────────────

import { createServerFn } from '@tanstack/react-start';
import { isServer } from '@tanstack/react-query';
import { parseRequest } from './server-functions-shared.server';
import { eventEmitter } from './server-broadcast.server';
import { clientTelemetryContext, telemetryCaller } from './wide-event.server';
import { allowTelemetryActions, allowTelemetryErrorReport } from './telemetry-rate-limit.server';
import {
	type ClientAction,
	type ClientErrorBreadcrumb,
	PORTAL_FRONTEND_ERROR_MARKER,
	recordClientActions,
	recordClientError,
	reportEventError,
	reportRawError,
} from '@worldcoo/monolith/wide-events';
import * as z from 'zod';
import { shouldReportFrontendError } from './frontend-error-filter';
import { portalLocalesExtended } from '@worldcoo/monolith/portals';

// ── Types ────────────────────────────────────────────────────────────────────

export type PutProjectErrorCode = 'PROJECT_TOO_LARGE' | 'SAVE_PROJECT_FAILED';
export type TranslateErrorCode = 'UNAUTHORIZED' | 'CAPTCHA_FAILED' | 'TRANSLATION_FAILED';
export type ContractErrorCode = 'NETWORK_ERROR' | 'CAPTCHA_FAILED' | 'CONTRACT_GENERATION_FAILED';
export type DraftContractErrorCode = 'DRAFT_NOT_FOUND' | 'CONTRACT_GENERATION_FAILED';
export type OnboardingDraftErrorCode = 'NETWORK_ERROR' | 'SAVE_FAILED';
export type LiveCounterMode = 'day' | 'realtime';

// ── Shared helpers ────────────────────────────────────────────────────────────

export async function ensureSeoData(context: { isBot: boolean }, promise: Promise<unknown>): Promise<void> {
	if (isServer && context.isBot) {
		await promise;
	}
}

const WINDOW_MS = 5 * 60_000;

/** Returns the current 5-minute window key (e.g. "2026-02-13T14:10") */
export function currentWindowKey() {
	const windowStart = Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
	return new Date(windowStart).toISOString().slice(0, 16);
}

// ── Server functions that are called directly (not through tRPC) ─────────────

export const getRequestContext = createServerFn()
	.validator(z.enum(portalLocalesExtended).optional())
	.handler(({ data }) => parseRequest(data));

/**
 * A user action leading up to a crash. Bounded and sanitized by the client before it is sent; the
 * `satisfies` is what ties this wire schema to the shape the recorder expects, so adding a field to one
 * without the other stops compiling.
 */
// Truncating, not `.max()`-rejecting, like every other free-text field here: one oversized crumb from a
// stale bundle must not void the report it rides on.
const breadcrumbSchema = z.object({
	t: z.number(),
	type: z.string().transform((value) => value.slice(0, 32)),
	message: z.string().transform((value) => value.slice(0, 200)),
}) satisfies z.ZodType<ClientErrorBreadcrumb>;

const clientActionSchema = z.object({
	t: z.number(),
	type: z.enum(['nav', 'click']),
	label: z.string().max(120),
	path: z.string().max(300),
}) satisfies z.ZodType<ClientAction>;

/**
 * Every string is length-bounded even though the browser half already truncates: this endpoint is
 * unauthenticated and reachable by anything that can POST, and its payload lands in a stderr line that
 * CloudWatch cuts at 256KB — an unbounded `message` or `stack` is a broken ticket. `catch` keeps a
 * malformed field from failing the whole report; `max` is enforced by truncating rather than rejecting,
 * for the same reason.
 */
const boundedErrorText = (max: number, fallback: string) =>
	z
		.string()
		.catch(fallback)
		.transform((value) => value.slice(0, max));

/**
 * The same bounding for a field that may be absent. No `.catch()`, unlike {@link boundedErrorText}: a
 * fallback would turn "not sent" into a literal placeholder string rather than leaving the field unset.
 */
const boundedOptionalErrorText = (max: number) =>
	z
		.string()
		.transform((value) => value.slice(0, max))
		.optional();

/**
 * Serialized-length budget shared by every big optional field on a report — stack, counters, cause and
 * breadcrumbs together, each measured as `JSON.stringify(field).length` so escaping is priced in (a
 * stack is mostly newlines away from doubling when embedded in the event JSON).
 *
 * Shared because `reportEventError` truncates the serialized event at `MAX_REPORT_LEN` (16_000), and a
 * truncated payload is no longer valid JSON: the ticket degrades to summary-only and the fingerprint
 * loses its stack branch, which is the exact failure this enrichment exists to prevent. Bounding only
 * stack + cause is not enough — breadcrumbs travel in the same line and alone serialize to ~6KB, so a
 * max-stack report with a full trail still overflowed. 11_000 leaves ~5KB for `message` (2_000),
 * route/name/source and the event's base attributes.
 *
 * Priority under pressure: stack first (a fingerprint input — losing it is what degrades a ticket), the
 * small counter snapshot next (it is the point of a hydration report), cause next (diagnosis), and
 * breadcrumbs last, dropping oldest-first (context).
 */
const CLIENT_FIELDS_BUDGET = 11_000;
const MAX_STACK_LEN = 9_000;
const MAX_CAUSE_LEN = 3_000;
const MAX_COUNTERS_LEN = 600;

/** Longest prefix of `value` whose JSON-serialized form fits `budget`, so escape inflation cannot cheat it. */
function fitSerialized(value: string, budget: number): string {
	let sliced = value.slice(0, Math.max(0, budget));
	let serialized = JSON.stringify(sliced).length;
	while (serialized > budget && sliced.length > 0) {
		// Shrink by the observed inflation ratio, not by the raw excess: an escape-heavy prefix (a stack
		// is mostly newlines) serializes at ~2x, and cutting the excess in characters would overshoot to
		// nothing instead of converging on the half that fits.
		sliced = sliced.slice(0, Math.floor((budget * sliced.length) / serialized));
		serialized = JSON.stringify(sliced).length;
	}
	return sliced;
}

/** Exported for its unit test: the shared budget below is the contract that keeps the report line parseable. */
export const logErrorInputSchema = z
	.object({
		route: boundedErrorText(300, 'unknown'),
		name: boundedErrorText(200, 'UnknownError'),
		message: boundedErrorText(2000, '(no message)'),
		// Truncated, not `.max()`-rejected: a rejection fails the whole report, losing a real crash.
		stack: boundedOptionalErrorText(MAX_STACK_LEN),
		// The wrapped error's `cause` chain (see `describeCauseChain`). Bounded far tighter than `stack`
		// because it is pure addition to a line that has to fit — see CLIENT_FIELDS_BUDGET.
		cause: boundedOptionalErrorText(MAX_CAUSE_LEN),
		// The hydration counter snapshot (see `counterSnapshot` in `src/client.tsx`). Its own field, not a
		// breadcrumb: as a 21st breadcrumb it tripped the array bound and voided the entire report, exactly
		// on the richest ones.
		counters: boundedOptionalErrorText(MAX_COUNTERS_LEN),
		// `recoverable` is the client entry's `onRecoverableError` (see `src/client.tsx`): a fault React
		// recovered from on its own, above all a hydration mismatch. Note this enum has no `.catch()`,
		// so an unlisted value fails validation and loses the whole report — add the member here before
		// sending a new one.
		source: z.enum(['boundary', 'global', 'unhandledrejection', 'recoverable']).optional(),
		// Sliced, never `.max()`-rejected, keeping the NEWEST: the actions just before the crash are the
		// ones that explain it.
		breadcrumbs: z
			.array(breadcrumbSchema)
			.transform((all) => all.slice(-20))
			.optional(),
	})
	// Charge the shared budget, in priority order — see CLIENT_FIELDS_BUDGET.
	.transform((data) => {
		let remaining = CLIENT_FIELDS_BUDGET;
		const spend = (value: string | undefined, max: number): string | undefined => {
			if (!value) return undefined;
			const fitted = fitSerialized(value, Math.min(max, remaining));
			if (!fitted) return undefined;
			remaining -= JSON.stringify(fitted).length;
			return fitted;
		};
		const stack = spend(data.stack, MAX_STACK_LEN);
		const counters = spend(data.counters, MAX_COUNTERS_LEN);
		const cause = spend(data.cause, MAX_CAUSE_LEN);
		const breadcrumbs = [...(data.breadcrumbs ?? [])];
		while (breadcrumbs.length > 0 && JSON.stringify(breadcrumbs).length > remaining) breadcrumbs.shift();
		return { ...data, stack, counters, cause, breadcrumbs: breadcrumbs.length > 0 ? breadcrumbs : undefined };
	});

export const logErrorFn = createServerFn({ method: 'POST' })
	.validator(logErrorInputSchema)
	.handler(({ data }) => {
		// Bounded per caller as well as per call. One report costs a queue message AND a ticketable stderr
		// line whose fingerprint the caller controls through the message, so an open endpoint with no
		// ceiling is an open supply of distinct Jira issues. Dropped silently: a failing telemetry call
		// would surface as another browser console error, which is the signal we are here to collect.
		//
		// Gated on the CHEAP caller lookup, before the full context is resolved, so a script hammering this
		// endpoint doesn't make us do the cookie and HMAC work its request will never use.
		if (!allowTelemetryErrorReport(telemetryCaller())) {
			return { success: true, logged: false, ticketed: false };
		}

		// Record the crash as a `client_error` wide event, and print that same event below. The event is
		// strictly richer than the ad-hoc context this used to assemble by hand — it adds the release the
		// browser was running, the locale/viewport/platform, the NGO session, the breadcrumb trail and the
		// trace id of the render that produced the page — so the ticket and the analytics row now describe
		// the crash identically instead of diverging.
		const event = recordClientError({ ...clientTelemetryContext(), ...data });

		// Server-side backstop for the noise filter the browser already applies before calling us.
		//
		// `shouldReportFrontendError` lives in the CLIENT bundle, so it only protects us for browsers
		// running a build that HAS it. A long-lived mobile tab keeps its bundle across our deploys, and the
		// injected-script junk of TEC-3648/3649/3650 kept arriving for a full day after the filter shipped
		// — every burst from a browser whose JS predated it (confirmed in the pro logs: the reports carry
		// none of the telemetry headers added the same morning, so those clients were provably stale).
		// A client-side-only filter can never close that window, because the code that would drop the
		// report is the code the stale client does not have.
		//
		// Applied to the TICKET only. The `client_error` event is already buffered above and stays that
		// way: `error = true` is still the right flag for an error-rate view, and dropping the analytics
		// row would hide how much of this traffic there is. Same split as `CLIENT_FAULT_RPC_CODES` in the
		// wide-event reporter — in the event, out of the ticket board.
		if (!shouldReportFrontendError(data.name, data.message, data.stack)) {
			return { success: true, logged: true, ticketed: false };
		}

		// Exactly ONE line may contain the marker: the parser tickets every occurrence of it, and a previous
		// structured-context line carrying a `type: 'PORTAL_FRONTEND_ERROR'` field was matched as a SECOND,
		// malformed ticket. Both branches go through `report-line.ts`, which owns the one-line rule.
		if (event) {
			reportEventError(event, { marker: PORTAL_FRONTEND_ERROR_MARKER });
		} else {
			// Telemetry disabled or the event failed to build — still ticket the crash, just thinly.
			reportRawError(PORTAL_FRONTEND_ERROR_MARKER, `${data.name} — ${data.message}`);
		}
		return { success: true, logged: true, ticketed: true };
	});

/**
 * Ingest for the session timeline. The browser batches lightweight nav/click actions and posts them
 * here; each becomes a `client_action` wide event. See `lib/session-actions.ts` for what the client
 * is (and is not) allowed to collect.
 *
 * Bounded at the boundary twice over, because every accepted action becomes its own queue message and
 * durable row. The validator caps ONE call (50 actions, every string length-limited); the rate limit
 * caps the CALLER across calls, which is what stops a script pointed at this open endpoint from
 * turning it into an amplifier. Excess is dropped silently rather than rejected: a failing telemetry
 * call would surface as a browser console error and pollute the signal we are here to collect.
 */
export const recordClientActionsFn = createServerFn({ method: 'POST' })
	.validator(z.object({ actions: z.array(clientActionSchema).max(50) }))
	.handler(({ data }) => {
		// Cheap gate first; the full context is only worth resolving for actions we are going to keep.
		const allowed = allowTelemetryActions(telemetryCaller(), data.actions.length);
		if (allowed > 0) {
			recordClientActions({
				...clientTelemetryContext(),
				actions: data.actions.slice(0, allowed),
				nowMs: Date.now(),
			});
		}
		return { success: true } as const;
	});

// ── Streaming server functions ───────────────────────────────────────────────

export const waitForConfirmedDonationFn = createServerFn()
	.validator(z.string())
	.handler(async function* ({ data: donationIdToConfirm }) {
		const promise = new Promise<'donation.confirmed' | 'donation.timeout'>((resolve) => {
			const onConfirmed = (donationId: string) => {
				if (donationId === donationIdToConfirm) {
					eventEmitter.off('donation.confirmed', onConfirmed);
					resolve('donation.confirmed');
				}
			};
			eventEmitter.on('donation.confirmed', onConfirmed);
			setTimeout(() => {
				eventEmitter.off('donation.confirmed', onConfirmed);
				resolve('donation.timeout');
			}, 10_000);
		});
		yield await promise;
	});

// ── Utility functions ────────────────────────────────────────────────────────

/**
 * Depth of the `cause` chain we walk. Three is plenty for the wrappers we see in practice (an
 * `AdyenCheckoutError` wrapping a DOMException wrapping nothing) and keeps a cyclic or absurdly deep
 * chain from turning one report into a wall of text.
 */
const MAX_CAUSE_DEPTH = 3;

/**
 * The `cause` chain of `error`, as its own reportable block, or `undefined` when there is none.
 *
 * Wrapped errors keep the diagnosis in `cause`: Adyen's Apple Pay layer reports
 * `AdyenCheckoutError('ERROR', 'ApplePay - Something went wrong…', { cause: theRealError })`, so without
 * this the ticket says "something went wrong" and the DOMException naming the fault is lost.
 *
 * Its OWN field, never folded into `message` or `stack`, because both feed `computeFingerprint` — a
 * per-occurrence cause in either re-tickets already-open issues — and `stack` also feeds
 * `isUnattributableThirdPartyError`, which would read grafted foreign frames as "none are ours" and drop
 * the report. Fully guarded: failing to enrich must never cost the report itself.
 */
function describeCauseChain(error: unknown): string | undefined {
	const parts: string[] = [];
	try {
		let current: unknown = error;
		for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
			if (!(current instanceof Error)) break;
			const cause: unknown = current.cause;
			if (cause === undefined || cause === null) break;
			if (cause instanceof Error) {
				parts.push(`Caused by: ${cause.name}: ${cause.message}\n${cause.stack ?? '(no stack)'}`);
			} else {
				parts.push(`Caused by: ${typeof cause === 'string' ? cause : safeStringify(cause)}`);
				break;
			}
			current = cause;
		}
	} catch {
		// A failure to enrich a report must never lose the report.
	}
	return parts.length === 0 ? undefined : parts.join('\n\n');
}

export interface ExtractedErrorInfo {
	name: string;
	message: string;
	stack: string | undefined;
	cause?: string;
}

export function extractErrorInfo(error: unknown): ExtractedErrorInfo {
	if (error instanceof Error) {
		return {
			name: error.name || 'Error',
			message: error.message || '(empty message)',
			stack: error.stack,
			cause: describeCauseChain(error),
		};
	}
	if (typeof error === 'string') {
		return { name: 'StringError', message: error, stack: undefined };
	}
	if (error === null || error === undefined) {
		return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined };
	}
	if (typeof error === 'object') {
		const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError';
		const message =
			'message' in error && typeof error.message === 'string'
				? error.message
				: 'error' in error && typeof error.error === 'string'
					? error.error
					: safeStringify(error);
		const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined;
		return { name, message, stack };
	}
	return { name: 'UnknownError', message: safeStringify(error), stack: undefined };
}

/**
 * The body every browser-side caller sends to {@link logErrorFn}: an extracted error under the call
 * site's `name` prefix, on the route it happened on.
 *
 * One builder for all of them so a new field is added once here instead of at every call site — adding
 * `cause` was a ten-site edit, and a site that gets missed silently reports less than its neighbours.
 * `extra` carries what a given caller genuinely adds (`source`, `breadcrumbs`, or a `stack` it augmented).
 */
export function errorReportData(
	namePrefix: string,
	info: ExtractedErrorInfo,
	extra?: Partial<z.input<typeof logErrorInputSchema>>,
): z.input<typeof logErrorInputSchema> {
	const { name, ...rest } = info;
	return { route: window.location.pathname, name: namePrefix + name, ...rest, ...extra };
}

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

// `shouldReportFrontendError` and the rest of the reporting predicates live in
// ./frontend-error-filter — a dependency-free module so they can be unit-tested without pulling in
// the server-function imports above. Import them from there directly, not through this module.
